import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useData, STAGES, blockers } from '../data.js';
import type { CompanyCard } from '../api.js';
import { seqState, seqRank } from './seq-state.js';
import { FitChip } from './Fit.js';

// ─────────────────────────────────────────────────────────────────────────────
// SELECTING ACCOUNTS TO WORK ON (2026-08-26).
//
// This screen could sort, filter and search twenty-eight accounts and do
// NOTHING to any of them. Enrichment lived entirely on "Build the list", which
// is where you go to find PEOPLE — so the only way to fill in firmographics was
// to go to a different screen and re-pick the accounts you were already looking
// at. Twenty-seven of Kyle's twenty-eight had never been enriched, which is
// what that costs.
//
// THE HANDOFF IS THE DESIGN, NOT A DODGE. Selection happens here, where the
// accounts are; the SPEND is confirmed on Build, where the cost line, the run
// progress and the credit ceiling already live. A one-click bulk purchase from
// a list view is exactly the shape of mistake the three separate enrich routes
// were split up to prevent. Build already reads `?slugs=`, so this adds no new
// spend path at all.
// ─────────────────────────────────────────────────────────────────────────────

type View = 'board' | 'table';
type SortKey = 'fit_score' | 'name' | 'priority' | 'stage' | 'sequence';
type SeqFilter = 'all' | 'active' | 'armed' | 'paused' | 'draft' | 'none';

function SeqChip({ c }: { c: CompanyCard }) {
  const s = seqState(c);
  return (
    <span className={`seqchip seq-${s.tone}`}>
      <b>{s.label}</b>
      {s.detail && <em>{s.detail}</em>}
    </span>
  );
}

function Card({ c }: { c: CompanyCard }) {
  const b = blockers(c);
  return (
    <Link className="card" to={`/companies/${c.slug}`}>
      <header>
        <h3>{c.name}</h3>
        {c.priority && <span className={`pri pri-${c.priority.toLowerCase()}`}>{c.priority}</span>}
      </header>
      <p className="meta">
        {c.fit ? <FitChip fit={c.fit} /> : c.fit_score !== null && <>fit {c.fit_score}</>}
        {c.fit_tier && <> · {c.fit_tier}-fit</>}
        {c.timing && <> · {c.timing}</>}
      </p>
      {c.gap_statement && <p className="gap">{c.gap_statement}</p>}
      {/* `.seqbar` was TWO different components sharing one class name — this
          4px progress bar, and the record's sequence control bar. The record's
          rule is later in the stylesheet, so it won: every card on this board
          was rendering its progress bar as a padded, bordered 31px box. Renamed
          rather than reordered, because the collision would just come back. */}
      {c.touches_total > 0 && (
        <div className="seqprogress"
          title={`${c.touches_written} of ${c.touches_total} touches written`}>
          <span style={{ width: `${(c.touches_written / c.touches_total) * 100}%` }} />
        </div>
      )}
      <p className="cardseq"><SeqChip c={c} /></p>
      {b.length > 0 && (
        <footer>
          {b.map((x) => (
            <span key={x.label} className={`flag ${x.hard ? 'flag-block' : 'flag-warn'}`}>
              {x.label}
            </span>
          ))}
        </footer>
      )}
    </Link>
  );
}

export function Companies() {
  const { companies } = useData();
  const nav = useNavigate();
  const [view, setView] = useState<View>('board');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('fit_score');
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  const [seqFilter, setSeqFilter] = useState<SeqFilter>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    if (!companies) return [];
    const needle = q.trim().toLowerCase();
    let out = companies.filter(
      (c) =>
        !needle ||
        c.name.toLowerCase().includes(needle) ||
        (c.gap_statement ?? '').toLowerCase().includes(needle),
    );
    if (onlyBlocked) out = out.filter((c) => blockers(c).some((b) => b.hard));
    // 'none' is its own answer, not the absence of one: 27 of 28 accounts have
    // no sequence at all, and "which have I not started" is the question that
    // actually fills a week.
    if (seqFilter !== 'all') {
      out = out.filter((c) => seqFilter === 'none'
        ? !c.sequence_status
        : c.sequence_status === seqFilter);
    }
    return [...out].sort((a, b) => {
      if (sort === 'sequence') {
        // Whatever is moving first, dormant last, then fit inside each band so
        // the top of a group is still the account worth the most.
        const d = seqRank(a) - seqRank(b);
        if (d) return d;
        return (b.fit?.score ?? b.fit_score ?? -1) - (a.fit?.score ?? a.fit_score ?? -1);
      }
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'priority') return (a.priority ?? 'P9').localeCompare(b.priority ?? 'P9');
      if (sort === 'stage') return a.stage.localeCompare(b.stage);
      // Disqualified sinks below unscored, which sinks below any real score.
      const rank = (x: typeof a) => x.fit?.excluded ? -2
        : x.fit?.score ?? (x.fit ? -1 : x.fit_score ?? -1);
      return rank(b) - rank(a);
    });
  }, [companies, q, sort, onlyBlocked, seqFilter]);

  if (!companies) return <p className="loading">Loading…</p>;

  return (
    <>
      <div className="toolbar">
        <input
          className="search"
          placeholder="Search accounts…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={onlyBlocked}
            onChange={(e) => setOnlyBlocked(e.target.checked)}
          />
          Blocked only
        </label>
        <select value={seqFilter}
          onChange={(e) => setSeqFilter(e.target.value as SeqFilter)}>
          <option value="all">Sequence: any</option>
          <option value="active">Sequence: active</option>
          <option value="armed">Sequence: armed</option>
          <option value="paused">Sequence: paused</option>
          <option value="draft">Sequence: draft</option>
          <option value="none">Sequence: not started</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
          <option value="fit_score">Sort: fit</option>
          <option value="sequence">Sort: sequence</option>
          <option value="name">Sort: name</option>
          <option value="priority">Sort: priority</option>
          <option value="stage">Sort: stage</option>
        </select>
        <div className="toggle">
          <button className={view === 'board' ? 'on' : ''} onClick={() => setView('board')}>
            Board
          </button>
          <button className={view === 'table' ? 'on' : ''} onClick={() => setView('table')}>
            Table
          </button>
        </div>
        <span className="count">{filtered.length} of {companies.length}</span>
        {/* Where new accounts come from, reachable from the screen where you
            notice you need them. Plain, not warm — the pipeline's primary
            action is working what is already here. */}
        <Link className="toolbtn" to="/source">+ Source accounts</Link>
      </div>

      {/* THE BAR ONLY EXISTS ONCE SOMETHING IS PICKED, and it counts what a run
          would actually buy rather than what is ticked. build-core skips an
          account that has already been enriched, so pricing the whole selection
          would overstate the cost and teach the operator to distrust the number. */}
      {picked.size > 0 && (() => {
        const rows = filtered.filter((c) => picked.has(c.id));
        const workable = rows.filter((c) => c.url);
        const unbought = workable.filter((c) => !c.enriched_at);
        const noUrl = rows.length - workable.length;
        return (
          <section className="panel">
            <h4>
              {/* rows.length, not picked.size: ticks hidden by the current
                  filter do not ride along into the build, so counting them in
                  the headline priced a different set than the button acts on. */}
              {rows.length} account{rows.length === 1 ? '' : 's'} selected
              <span className="count">
                {unbought.length === 0
                  ? 'all already enriched · finding people is free'
                  : `${unbought.length} never enriched · up to ${unbought.length} Apollo credit`
                    + `${unbought.length === 1 ? '' : 's'}`}
              </span>
            </h4>
            <div className="actions">
              <button
                disabled={workable.length === 0}
                onClick={() => nav(`/build?slugs=${workable.map((c) => c.slug).join(',')}&enrich=1`)}>
                Enrich and find people at {workable.length} account
                {workable.length === 1 ? '' : 's'}
              </button>
              <button className="mini" onClick={() => setPicked(new Set())}>Clear</button>
            </div>
            <p className="fieldhint">
              Opens Build with these ticked. Nothing is bought until you start the
              run there, and the people search is free either way.
              {noUrl > 0 && ` ${noUrl} of them has no website and cannot be worked at all.`}
              {workable.length > 25 && ' A single run takes 25 at most.'}
            </p>
          </section>
        );
      })()}

      {view === 'board' ? (
        <div className="board">
          {STAGES.map((stage) => {
            const inStage = filtered.filter((c) => c.stage === stage.key);
            if (!inStage.length) return null;
            return (
              <section key={stage.key} className="column">
                <h2>{stage.label} <span className="count">{inStage.length}</span></h2>
                {inStage.map((c) => <Card key={c.id} c={c} />)}
              </section>
            );
          })}
        </div>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th className="tick">
                {/* Only what is VISIBLE. A header tick that silently includes
                    rows a filter is hiding is how somebody buys twenty-eight
                    credits meaning to buy four. */}
                <input
                  type="checkbox"
                  aria-label="Select all shown"
                  checked={filtered.length > 0 && filtered.every((c) => picked.has(c.id))}
                  onChange={(e) => setPicked(e.target.checked
                    ? new Set(filtered.map((c) => c.id))
                    : new Set())}
                />
              </th>
              <th>Account</th><th>Stage</th><th>Pri</th><th>Fit</th>
              <th>Timing</th><th>Enriched</th><th>Sequence</th><th>Blocked on</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const b = blockers(c);
              return (
                <tr key={c.id}>
                  <td className="tick">
                    <input
                      type="checkbox"
                      aria-label={`Select ${c.name}`}
                      checked={picked.has(c.id)}
                      onChange={(e) => setPicked((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(c.id); else next.delete(c.id);
                        return next;
                      })}
                    />
                  </td>
                  <td><Link to={`/companies/${c.slug}`}>{c.name}</Link></td>
                  <td className="meta">{c.stage.replace('_', ' ')}</td>
                  <td>{c.priority && <span className={`pri pri-${c.priority.toLowerCase()}`}>{c.priority}</span>}</td>
                  <td>{c.fit ? <FitChip fit={c.fit} /> : (c.fit_score ?? '—')}</td>
                  <td className="meta">{c.timing ?? '—'}</td>
                  <td>
                    {!c.url
                      ? <span className="flag flag-block">no website</span>
                      : c.enriched_at
                        ? <span className="flag flag-ok">enriched</span>
                        : <span className="flag flag-warn">never</span>}
                  </td>
                  <td><SeqChip c={c} /></td>
                  <td>
                    {b.map((x) => (
                      <span key={x.label} className={`flag ${x.hard ? 'flag-block' : 'flag-warn'}`}>
                        {x.label}
                      </span>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
